import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { PlatformHttpService } from '../../../services/platform-http.service';
import {
  HarvestRecordDto,
  HarvestRecordAnalyticsDto,
  CreateHarvestRecordRequest,
  UpdateHarvestRecordRequest,
  HarvestRecordFilters
} from '../models';
import { AuditService } from '../../../shared/models/audit-service.interface';
import { PlatformAuditResponse } from '../../../shared/models/platform-audit.model';
import { Page } from '../../../shared/models/page.model';

/**
 * Harvest Record Service - handles all harvest-record-related API calls
 */
@Injectable({ providedIn: 'root' })
export class HarvestRecordService implements AuditService {
  private platformHttp = inject(PlatformHttpService);
  private readonly baseUrl = '/harvest-record';

  /**
   * Get all harvest records
   */
  getAllHarvestRecords(): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(this.baseUrl) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest record by ID
   */
  getHarvestRecordById(id: number): Observable<HarvestRecordDto> {
    return this.platformHttp.get(`${this.baseUrl}/${id}`) as Observable<HarvestRecordDto>;
  }

  /**
   * Create harvest record
   */
  createHarvestRecord(request: CreateHarvestRecordRequest): Observable<any> {
    // Returns 201 Created with Location header
    return this.platformHttp.post(this.baseUrl, request, undefined, undefined, true);
  }

  /**
   * Update harvest record
   */
  updateHarvestRecord(request: UpdateHarvestRecordRequest): Observable<void> {
    return this.platformHttp.put(`${this.baseUrl}/${request.id}`, request) as Observable<void>;
  }

  /**
   * Delete harvest record
   */
  deleteHarvestRecord(id: number): Observable<void> {
    return this.platformHttp.delete(`${this.baseUrl}/${id}`) as Observable<void>;
  }

  /**
   * Get harvest records by batch
   */
  getHarvestRecordsByBatch(batchId: number): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/batch/${batchId}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by date
   */
  getHarvestRecordsByDate(recordDate: string): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/date/${recordDate}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by date range
   */
  getHarvestRecordsByDateRange(startDate: string, endDate: string): Observable<HarvestRecordDto[]> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    
    return this.platformHttp.get(`${this.baseUrl}/date-range`, params) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by user assignee
   */
  getHarvestRecordsByAssigneeUser(userId: number): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/assignee/user/${userId}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by worker assignee
   */
  getHarvestRecordsByAssigneeWorker(workerId: number): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/assignee/worker/${workerId}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by variety
   */
  getHarvestRecordsByVariety(varietyId: number): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/variety/${varietyId}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get harvest records by production activity
   */
  getHarvestRecordsByProductionActivity(productionActivityId: number): Observable<HarvestRecordDto[]> {
    return this.platformHttp.get(`${this.baseUrl}/production-activity/${productionActivityId}`) as Observable<HarvestRecordDto[]>;
  }

  /**
   * Get filtered harvest records
   */
  getHarvestRecords(filters: HarvestRecordFilters): Observable<HarvestRecordDto[]> {
    if (filters.startDate && filters.endDate) {
      return this.getHarvestRecordsByDateRange(filters.startDate, filters.endDate);
    } else if (filters.recordDate) {
      return this.getHarvestRecordsByDate(filters.recordDate);
    } else if (filters.batchId) {
      return this.getHarvestRecordsByBatch(filters.batchId);
    } else if (filters.userId) {
      return this.getHarvestRecordsByAssigneeUser(filters.userId);
    } else if (filters.workerId) {
      return this.getHarvestRecordsByAssigneeWorker(filters.workerId);
    } else if (filters.varietyId) {
      return this.getHarvestRecordsByVariety(filters.varietyId);
    } else if (filters.productionActivityId) {
      return this.getHarvestRecordsByProductionActivity(filters.productionActivityId);
    }
    return this.getAllHarvestRecords();
  }

  /**
   * Server-side paginated + filtered harvest records (date range, parcel, zero-weight, search).
   */
  searchHarvestRecords(
    filters: { startDate?: string; endDate?: string; parcelId?: number; includeZeroWeight?: boolean; q?: string },
    page: number,
    size: number
  ): Observable<Page<HarvestRecordDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('includeZeroWeight', (filters.includeZeroWeight ?? false).toString());
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.parcelId) params = params.set('parcelId', filters.parcelId.toString());
    if (filters.q && filters.q.trim()) params = params.set('q', filters.q.trim());

    return this.platformHttp.get(`${this.baseUrl}/paginated`, params) as Observable<Page<HarvestRecordDto>>;
  }

  /**
   * Get harvest record analytics
   */
  getAnalytics(params: {
    userId?: number;
    workerId?: number;
    teamId?: number;
    startDate?: string;
    endDate?: string;
    parcelId?: number;
    includeZeroWeight?: boolean;
    q?: string;
  }): Observable<HarvestRecordAnalyticsDto> {
    let httpParams = new HttpParams();
    if (params.userId) httpParams = httpParams.set('userId', params.userId.toString());
    if (params.workerId) httpParams = httpParams.set('workerId', params.workerId.toString());
    if (params.teamId) httpParams = httpParams.set('teamId', params.teamId.toString());
    if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params.parcelId) httpParams = httpParams.set('parcelId', params.parcelId.toString());
    httpParams = httpParams.set('includeZeroWeight', (params.includeZeroWeight ?? false).toString());
    if (params.q && params.q.trim()) httpParams = httpParams.set('q', params.q.trim());

    return this.platformHttp.get(`${this.baseUrl}/analytics`, httpParams) as Observable<HarvestRecordAnalyticsDto>;
  }

  // AuditService implementation
  getAuditHistoryPaginated(entityId: number, page: number = 0, size: number = 20): Observable<Page<PlatformAuditResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    
    return this.platformHttp.get(`${this.baseUrl}/${entityId}/audit`, params) as Observable<Page<PlatformAuditResponse>>;
  }
}

